import {
    DestroyRef,
    Directive,
    inject,
    input,
    type OnInit,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    hasPermission,
    type HasPermissionType,
    injectUser,
} from '@quipex/core/auth';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';

/**
 * A structural directive that conditionally renders content based on user permissions.
 *
 * Uses strict TypeScript types to ensure compile-time validation of permission patterns.
 * Delegates to the centralized `hasPermission` utility for consistent permission checking.
 *
 * @example
 * ```html
 * <!-- Show content for users with 'OrgUsersRead' permission -->
 * <div *hasPermission="['OrgUsersRead']">User management content</div>
 *
 * <!-- Show content for users with any of the specified permissions -->
 * <div *hasPermission="['OrgUsersInvite', 'OrgUsersRoleUpdate']">User management actions</div>
 *
 * <!-- Multiple permissions (OR logic) -->
 * <div *hasPermission="['OrgUsersRead', 'OrgUsersInvite', 'OrgUsersRoleUpdate']">User access</div>
 *
 * <!-- AND logic with inner arrays -->
 * <div *hasPermission="[['OrgUsersRead', '!OrgUsersDelete']]">User management without delete</div>
 *
 * <!-- Mixed patterns -->
 * <div *hasPermission="[['OrgUsersRead', '!OrgUsersDelete'], 'OrgUsersInvite']">Complex permission logic</div>
 * ```
 *
 * @usageNotes
 *
 * ### Permission Matching Logic
 * - **Exact match**: `['OrgUsersRead']` - matches users with exactly 'OrgUsersRead' permission
 * - **Negation**: `['!OrgUsersDelete']` - matches users who do NOT have 'OrgUsersDelete' permission
 * - **Multiple permissions**: `['OrgUsersRead', 'OrgUsersRoleUpdate']` - matches if user has ANY of these permissions (OR)
 * - **AND groups**: `[['OrgUsersRead', '!OrgUsersDelete']]` - matches if user has OrgUsersRead AND does NOT have OrgUsersDelete
 * - **Case-sensitive**: Permission matching is case-sensitive and uses canonical `PermissionKey` values
 *
 * ### Special Cases
 * - Empty permissions array will hide the content
 * - Missing user permissions will hide the content
 * - Delegates to centralized `hasPermission` utility for consistent behavior
 */
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[hasPermission]',
})
export class HasPermission implements OnInit {
    private readonly templateRef = inject(TemplateRef);
    private readonly viewContainer = inject(ViewContainerRef);
    private readonly me = injectUser();
    private readonly destroyRef = inject(DestroyRef);

    public readonly hasPermission = input.required<HasPermissionType>();

    private readonly hasPermission$ = toObservable(this.hasPermission);
    private readonly currentUser$ = this.me.required$;

    ngOnInit(): void {
        this.shouldShowView();
    }

    private shouldShowView() {
        combineLatest([this.hasPermission$, this.currentUser$])
            .pipe(
                map(([permissions, user]) => hasPermission(user, permissions)),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((shouldShow) => {
                this.updateView(shouldShow);
            });
    }

    private updateView(shouldShow: boolean): void {
        this.viewContainer.clear();
        if (shouldShow) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
    }
}
